'use client';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { gridClassName } from '../constants';
import {
  Alert,
  Button,
  Checkbox,
  InputNumber,
  Favorite,
  Spinner,
  Skeleton,
  Tooltip,
  GlobalLoading,
  Modal,
} from '@/shared/components';
import { Availability, Product } from '@/types/cart';
import { usePriceStore } from '../../../store/price-store';
import { equal, multiply, number, subtract } from 'mathjs';
import { useDebounce } from 'ahooks';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import { useAppStore } from '@/store/app-store';
import { useProductStore } from '../product-store';
import { useCartStore } from '../cart-store';
import { cartService } from '@/services/cart.service';
import { updateFavorite } from '@/app/utils/check-favorite';
import { processNumberString, redirectLoginPage } from '@/app/utils';
import { ProductImage } from '@/shared/pageComponents';
import { usePriceFormat } from '@/hooks/use-price-format';
import HoldCodeMessage from '@/shared/pageComponents/hold-code-message';
import { useQueryHoldCode, useMutationStock } from '@/hooks/product-detail.hook';
import Link from 'next/link';
import { usePlacement, useProductClickEventTracking } from '@/shared/tracking/use-tracking-helpers';
import { useEventTracking } from '@/shared/tracking/use-tracking';
import { isLocalSource } from '@/app/utils/check-local-source';
import { buildPdpLink } from '@/app/utils/build-pdp-link';
import { getContactUsLink } from '@/app/utils/get-contact-us-link';
import { useRouter } from 'next/navigation';
import { useCountry } from '@/hooks/use-country';
import { CartQtpModel } from './cart-qtp-model';
import { evaluateManualPromotionPrice, syncPrice } from '@/app/utils/price-availability';

interface IProps {
  index: number;
  product: Product;
  isFavorite?: boolean;
  updateQuantity?: any;
  updateInit?: any;
}

export const ShoppingItem = ({ index, product, isFavorite, updateQuantity, updateInit }: IProps) => {
  const { isCN, isKR } = useCountry();
  const router = useRouter();
  const { isAnonymous } = useAppStore();
  const productPrice = usePriceStore((state) => state.productPrice);
  const promotionInfo = usePriceStore((state) => state.promotionInfo);
  const fbpSkuInfo = usePriceStore((state) => state.fbpSkuInfo);
  //const price = productPrice?.[product.catalogNumber];
  const { t, lang } = useLang(NAMESPACE.cart);
  const { t: commonT } = useLang(NAMESPACE.common);
  const { t: promotionT } = useLang(NAMESPACE.promotion);
  const stockData = useMutationStock();
  const cartStore = useCartStore();
  const productStore = useProductStore();
  const [price, setPrice] = useState(productPrice?.[product.catalogNumber]);
  const [quantity, setQuantity] = useState<number>(product.quantity);
  const [qtpConfirmModalInfo, setQtpConfirmModalInfo] = useState<{
    type: 'remove' | 'changeQuantity';
    quantity?: number;
    status: 'mandatory' | 'optional';
  }>();

  const changeEventTigerRef = useRef(false);

  const [availability, setAvailability] = useState<Availability>();
  const getFormattedPrice = usePriceFormat();
  const [isFav, setIsFav] = useState<boolean>();
  const [isOpen, setIsOpen] = useState(false);
  const [showLoader, setLoader] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [stockDataResponse, setStockDataResponse] = useState<any>(null);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockVal, setStockVal] = useState<number | null>(null);

  const isSelected = cartStore.selectedCatalogNumbers.includes(product.catalogNumber);
  const { data: holdCodeResponse } = useQueryHoldCode(product.catalogNumber, isSelected);

  const { trackEvent } = useEventTracking();
  const placement = usePlacement();
  useEffect(() => {
    setIsFav(isFavorite);
  }, [isFavorite]);
  useEffect(() => {
    if (!isAnonymous && product.productStatus !== 'BLOCKED') {
      setAvailabilityLoading(true);
      cartStore.removeAvailabilityByCatalogNumber(product.catalogNumber);
      cartService
        .getProductsAvailability({
          catalogNumber: product.catalogNumber,
          requestQuantity: product.quantity,
        })
        .then((response) => {
          setAvailability(response);
          if (promotionInfo?.promoType === 'FBP' && product?.canApplyPromotion !== undefined) {
            cartStore.addAvailabilityByCatalogNumber(
              response,
              product.catalogNumber + '_' + product?.canApplyPromotion
            );
          }
          cartStore.addAvailabilityByCatalogNumber(response, product.catalogNumber);
        })
        .catch(() => {
          cartStore.addAvailabilityByCatalogNumber(null, product.catalogNumber);
        })
        .finally(() => {
          setAvailabilityLoading(false);
        });
    }
  }, [product.catalogNumber, product.quantity, isAnonymous]);
  useEffect(() => {
    if (productPrice?.[product.catalogNumber]) {
      setPrice(productPrice[product.catalogNumber]);
    }
  }, [productPrice]);
  useEffect(() => {
    if (!product.canApplyPromotion) return;
    if (!fbpSkuInfo) return;
    if (promotionInfo && promotionInfo?.promoType === 'FBP' && product?.canApplyPromotion !== undefined) {
      setPrice(fbpSkuInfo[product?.catalogNumber + '_' + product?.canApplyPromotion]);
    }
  }, [fbpSkuInfo]);

  useEffect(() => {
    if (!isKR || !product?.catalogNumber) return;
    setStockLoading(true);
    stockData
      .mutateAsync([{ catalogNumber: product.catalogNumber }])
      .then((response: any) => {
        setStockDataResponse(response?.data ?? null);
      })
      .catch(() => {
        setStockDataResponse(null);
      })
      .finally(() => {
        setStockLoading(false);
      });
  }, [isKR, product.catalogNumber]);

  useEffect(() => {
    if (!isKR) return;
    if (stockDataResponse && stockDataResponse.length > 0) {
      const currentStock = stockDataResponse.find((item) => item.catalogNumber === product.catalogNumber);
      setStockVal(currentStock?.domesticStock ?? null);
      return;
    }
    setStockVal(null);
  }, [isKR, stockDataResponse, product.catalogNumber]);
  const onQuantityChange = (value: string, enableCheck = true) => {
    if (enableCheck && isQuantityLessThanQTP(processNumberString(value))) {
      return;
    }
    setQuantity(processNumberString(value));
    changeEventTigerRef.current = true;
  };

  const debounceQuantity = useDebounce(quantity, {
    wait: 300,
  });

  useEffect(() => {
    if (
      debounceQuantity > 0 &&
      changeEventTigerRef.current &&
      !equal(number(debounceQuantity), number(product.quantity))
    ) {
      changeEventTigerRef.current = false;
      const promoMessage = promotionInfo?.promotionMessages?.find(
        (item) => item.catalogNumber === product.catalogNumber && !!item.messageCode
      );
      const promoQuantityMessage = promotionInfo?.promotionMessages?.find(
        (item) => item.catalogNumber === product.catalogNumber && item.promotionQuantity != null
      );
      const promoMessageCode = promoMessage?.messageCode?.toUpperCase();
      const isADPQuantityRefreshMessage =
        promoMessageCode === 'ADP_MORE_QUANTITY_REQUIRED' || promoMessageCode === 'ADP_PROMOTION_APPLIED';
      const shouldRefreshPriceForPromotionMessage =
        isSelected && !!promoMessageCode && (promoMessageCode.startsWith('VBP') || isADPQuantityRefreshMessage);

      if (
        promotionInfo?.promoType === 'FBP' ||
        (promotionInfo?.promoType === 'STP' &&
          productPrice?.[product.catalogNumber]?.includePromotion === promotionInfo?.promoCode)
      ) {
        updateQuantity();
      } else if (shouldRefreshPriceForPromotionMessage) {
        if (promotionInfo?.promoCode && !isADPQuantityRefreshMessage) {
          void evaluateManualPromotionPrice(
            [
              {
                catalogNumber: product.catalogNumber,
                webCatalogNumber: product.webCatalogNumber,
                quantity: number(debounceQuantity),
              },
            ],
            cartStore.address?.accountNumber,
            { promotionCode: promotionInfo.promoCode }
          );
        } else {
          void syncPrice(
            [
              {
                childCatalogNumber: product.catalogNumber,
                skuStatus: product.productStatus,
                webCatalogNumber: product.webCatalogNumber,
                quantity: number(debounceQuantity),
              },
            ],
            cartStore.address?.accountNumber,
            undefined,
            [product.catalogNumber]
          );
        }
      }
      if (promoQuantityMessage) {
        productStore.changeQuantity(
          product.catalogNumber,
          number(debounceQuantity),
          promoQuantityMessage?.promotionQuantity
        );
      } else {
        productStore.changeQuantity(product.catalogNumber, number(debounceQuantity));
      }
    }
  }, [debounceQuantity]);

  const onRemoveProduct = async (enableCheck = true) => {
    const qtpSKUMap = usePriceStore.getState()?.promotionInfo?.qtpSKUMap?.[product.catalogNumber];
    if (qtpSKUMap?.minQuantity && enableCheck) {
      setQtpConfirmModalInfo({
        type: 'remove',
        status: qtpSKUMap.required ? 'mandatory' : 'optional',
      });
      return;
    }

    GlobalLoading.show();
    trackEvent({
      key: 'cart',
      action: 'scRemove',
      payload: {
        products: [
          {
            name: product?.productTitle,
            catalogId: product.catalogNumber,
          },
        ],
      },
    });

    await productStore.removeProduct(product.catalogNumber);
    GlobalLoading.hide();
  };

  const updateFav = () => {
    setLoader(true);
    updateFavorite([product.catalogNumber], isFav).then((res: any) => {
      if (res.code === '200' && res.message === 'success') {
        setIsFav(!isFav);
        setIsOpen(true);
        setLoader(false);
        setTimeout(() => {
          setIsOpen(false);
        }, 5e3);
        !isFav &&
          trackEvent({
            key: 'eventsData',
            action: 'addToFavoritesList',
            payload: {
              event_name: 'addToFavoritesList',
              contentType: 'Product',
              products: [
                {
                  catalogId: product.catalogNumber,
                  quantity: product.quantity,
                  price: productPrice?.[product.catalogNumber]?.bestPrice,
                  placement,
                },
              ],
            },
          });
        isFav &&
          trackEvent({
            key: 'eventsData',
            action: 'removeFromFavoritesList',
            payload: {
              event_name: 'removeFromFavoritesList',
              contentType: 'Product',
              products: [
                {
                  catalogId: product.catalogNumber,
                  placement,
                },
              ],
            },
          });
      }
    });
  };

  const onPriceInputBlur = () => {
    if (quantity === 0) {
      setQuantity(product.quantity);
    }
  };

  useEffect(() => {
    if (product.productStatus === 'BLOCKED' && product.quantity !== 1) {
      onQuantityChange('1');
      return;
    }
    setQuantity(product.quantity);
  }, [product.quantity, product.productStatus]);

  const onSelectProduct = () => {
    if (
      !contactUs &&
      ((availability && availability.availabilityMessage[lang.toUpperCase()]?.length > 0) ||
        (!availability && isAnonymous))
    ) {
      cartStore.changeSelectedProducts(product.catalogNumber);
    }
  };

  const savings = useMemo(() => {
    if (!price) return 0;
    return subtract(number(price.listPrice), number(price.bestPrice));
  }, [price]);

  const contactUs = useMemo(() => {
    if (product.productStatus === 'BLOCKED') return true;
    if (!price) return false;
    if (product.entitled === false) {
      return true;
    }
    if (price.priceStatus !== 'Orderable') {
      return true;
    }

    if (!price.listPrice || number(price?.listPrice ?? 0) === 0) {
      return true;
    }

    if (!availability) return false;

    return product.dwgFlag === 'Y' && availability?.totalStock === 0 && !isLocalSource(product.localSource);
  }, [price, availability, product.productStatus]);

  const firstInit = useRef(true);
  const checkboxDisabled = useMemo(() => {
    return (
      !price ||
      contactUs ||
      product.productStatus !== 'RELEASED' ||
      (!availability && !isAnonymous) ||
      (availability && availability.availabilityMessage[lang.toUpperCase()]?.length === 0)
    );
  }, [price, contactUs, availability, product.productStatus, lang]);
  useEffect(() => {
    if (!checkboxDisabled && !cartStore.selectedCatalogNumbers.includes(product.catalogNumber) && firstInit.current) {
      firstInit.current = false;
      updateInit(false);
      cartStore.changeSelectedProducts(product.catalogNumber);
    }

    if (checkboxDisabled && cartStore.selectedCatalogNumbers.includes(product.catalogNumber)) {
      cartStore.changeSelectedProducts(product.catalogNumber);
    }
  }, [checkboxDisabled]);

  const promotionChanged = useRef(false);
  useEffect(() => {
    if (promotionInfo?.promoCode) {
      promotionChanged.current = true;
    } else if (promotionChanged.current && !promotionInfo?.promoCode) {
      promotionChanged.current = false;
      firstInit.current = true;
      updateInit(true);
    }
  }, [promotionInfo?.promoCode]);

  const productClickEventTracking = useProductClickEventTracking();

  const isQuantityLessThanQTP = (quantity) => {
    const qtpSKUMap = usePriceStore.getState()?.promotionInfo?.qtpSKUMap?.[product.catalogNumber];
    if (qtpSKUMap?.minQuantity && quantity < qtpSKUMap.minQuantity) {
      setQtpConfirmModalInfo({
        type: 'changeQuantity',
        quantity,
        status: qtpSKUMap.required ? 'mandatory' : 'optional',
      });
      return true;
    }
    return false;
  };
  const getFBPItems = () => {
    const promoMessage = promotionInfo?.promotionMessages?.find(
      (item) => item.catalogNumber === product.catalogNumber && item.promotionQuantity != null
    );
    if (promoMessage) {
      return promoMessage['promotionQuantity'] || null;
    }
    return null;
  };
  const productPromotionMessages =
    promotionInfo?.promotionMessages?.filter(
      (item) => item.catalogNumber === product.catalogNumber && !!item.messageCode
    ) ?? [];
  const vbpAppliedPromotionMessage =
    productPromotionMessages.find((item) => item.messageCode === 'VBP_PROMOTION_APPLIED')?.message || null;
  const vbpNextSetPromotionMessage =
    productPromotionMessages.find((item) => item.messageCode === 'VBP_NEXT_SET_QUANTITY_REQUIRED')?.message || null;
  const vbpPromotionMessage =
    productPromotionMessages.find(
      (item) =>
        item.messageCode === 'VBP_PROMOTION_APPLIED' ||
        item.messageCode === 'VBP_MORE_QUANTITY_REQUIRED' ||
        item.messageCode === 'VBP_LOWER_PROMO_PRICE_APPLIED' ||
        item.messageCode === 'VBP_NEXT_SET_QUANTITY_REQUIRED'
    )?.message || null;
  const adpPromotionMessage =
    productPromotionMessages.find((item) => item.messageCode === 'ADP_PROMOTION_APPLIED')?.message || null;
  const defaultPromotionMessage =
    productPromotionMessages.find(
      (item) =>
        item.messageCode !== 'VBP_PROMOTION_APPLIED' &&
        item.messageCode !== 'VBP_MORE_QUANTITY_REQUIRED' &&
        item.messageCode !== 'VBP_LOWER_PROMO_PRICE_APPLIED' &&
        item.messageCode !== 'VBP_NEXT_SET_QUANTITY_REQUIRED' &&
        item.messageCode !== 'ADP_PROMOTION_APPLIED' &&
        item.messageCode !== 'ADP_MORE_QUANTITY_REQUIRED'
    )?.message || null;
  const getPromotionMessage = () => {
    if (promotionInfo?.promoType === 'FBP' && price?.canApplyPromotion !== false) {
      return null;
    }

    return defaultPromotionMessage;
  };
  const getPromotionQuantity = () => {
    const promoMessage = promotionInfo?.promotionMessages?.find(
      (item) => item.catalogNumber === product.catalogNumber && item.promotionQuantity != null
    );
    if (promoMessage) {
      return promoMessage['promotionQuantity'] || null;
    }
    return null;
  };
  const effectivePromoType = price?.appliedPromoType || promotionInfo?.promoType;
  const isVBPAppliedPromo = price?.appliedPromoType === 'VBP';
  const effectivePromoNotApplicableReason =
    price?.manualPromoNotApplicableReasonCode || price?.promoNotApplicableReason || null;
  const hasAppliedCartPromotion = !!promotionInfo?.promoType;
  const shouldForcePromoNotApplicable =
    (!!vbpAppliedPromotionMessage || !!vbpNextSetPromotionMessage) && hasAppliedCartPromotion;
  const effectiveDiscount = shouldForcePromoNotApplicable ? null : price?.discount != null ? price.discount : null;
  const shouldShowDiscountByAppliedPromoCode =
    !!price?.appliedPromoCode && price?.appliedPromoType !== 'ADP' && price?.finalPriceSource !== 'ADP';
  const hasLocalPromotionContext = !!promotionInfo?.promoCode;
  const shouldShowManualPromoNotApplicable = !!effectivePromoNotApplicableReason && hasLocalPromotionContext;
  const showDiscountDetails =
    !shouldForcePromoNotApplicable &&
    !isVBPAppliedPromo &&
    !!effectivePromoType &&
    (price?.canApplyPromotion || price?.manualPromoApplied || shouldShowDiscountByAppliedPromoCode) &&
    effectiveDiscount != null &&
    effectiveDiscount >= 0;
  const showDiscountTooltip = price?.originalDiscount != null || price?.additionalDiscount != null;
  const discountBadge = (
    <div className="flex flex-row justify-center items-center rounded-[15px] border-[1px] border-solid border-[#FB9090] px-3">
      {isCN && effectivePromoType === 'STP' ? (
        <span className="font-normal">{promotionT('product.stp.discount', { discount: effectiveDiscount ?? 0 })}</span>
      ) : effectivePromoType === 'QTP' && !effectiveDiscount ? (
        promotionT('promotion.qtp.discount')
      ) : (
        <>
          <span className="font-medium ">{promotionT('promotion.pricing.discount')}</span>
          <span className="font-normal ml-1">{effectiveDiscount ?? 0}%</span>
        </>
      )}
    </div>
  );
  const discountTooltipTitle = (
    <div className="space-y-1">
      {price?.originalDiscount != null && (
        <p>{promotionT('promotion.pricing.original.discount', { discount: price.originalDiscount })}</p>
      )}
      {price?.additionalDiscount != null && (
        <p>{promotionT('promotion.pricing.additional.discount', { discount: price.additionalDiscount })}</p>
      )}
    </div>
  );
  return (
    <div
      className={`${gridClassName} border-0 border-b border-solid border-gray-300 mt-6 pb-6 last:border-b-0 shopping-cart-item`}
      style={{
        pageBreakBefore: 'auto',
      }}
    >
      <Checkbox
        className="color-adjust"
        checked={cartStore.selectedCatalogNumbers.includes(product.catalogNumber)}
        onChange={onSelectProduct}
        disabled={checkboxDisabled}
      />
      <ProductImage className="sm:mb-4" src={product.productImage} key={product.catalogNumber} width={90} height={90} />
      <div className="flex flex-col sm:row-start-2 sm:col-start-2">
        {/* there is no need to add i18 */}
        <span
          dangerouslySetInnerHTML={{ __html: product.brandImage ?? 'Thermo Scientific&trade;' }}
          className="text-xs font-normal text-gray-900"
        ></span>
        <Link
          type="link"
          prefetch
          onClick={(e) => {
            productClickEventTracking(product, index);
          }}
          href={buildPdpLink(product.catalogNumber)}
          className="p-0 text-left mt-[6px] flex items-center h-auto cursor-pointer disabled:cursor-not-allowed disabled:bg-transparent disabled:border-none disabled:text-[#A5A5AE]"
        >
          <span
            className="leading-none text-base font-bold"
            dangerouslySetInnerHTML={{ __html: product.productTitle }}
          ></span>
        </Link>
        <span className="mt-1">
          <span className="text-sm font-normal">{commonT('catalog.number')}:</span>
          <span className="ml-2 text-sm font-bold">{product.webCatalogNumber}</span>
        </span>
        <span className="mt-1">
          <span className="text-sm font-normal">{t('unit.size')}</span>
          <span className="ml-2 text-sm font-bold" dangerouslySetInnerHTML={{ __html: product.unitSize }}></span>
        </span>
      </div>
      <div className="flex gap-x-6 items-center mt-4 print:hidden col-start-3 row-start-2 lg:row-start-3 sm:row-start-6 md:row-start-3 sm:col-start-2 sm:col-span-2 sm:justify-between">
        <Button
          type="link"
          className="p-0 text-left flex items-center h-auto leading-none relative"
          onClick={onRemoveProduct}
        >
          <i className="icon-24-bin-mono" />
          <span className="ml-[6px]">{t('delete')}</span>
        </Button>
        {!isAnonymous && (
          <>
            <div className="relative">
              <Button
                type="link"
                className="p-0 text-left flex items-center h-auto leading-none relative"
                onClick={updateFav}
              >
                {isFav ? <i className="icon-24-favorites-bold" /> : <i className="icon-24-favorites-mono" />}
                <span className="ml-[6px]">{isFav ? t('saved.in.list') : t('save.in.list')}</span>
              </Button>
              <div className="absolute left-2/4">
                <Spinner isLoading={showLoader} />
              </div>
            </div>
            <Favorite
              isFav={isFav}
              isOpen={isOpen}
              title={
                isFav
                  ? commonT('favorite.added').replace('%s', product.webCatalogNumber)
                  : commonT('favorite.removed').replace('%s', product.webCatalogNumber)
              }
              closeFunc={() => {
                setIsOpen(false);
              }}
            />
          </>
        )}
      </div>

      <div className="flex flex-col lg:col-start-3 lg:row-start-2  md:col-start-3 md:row-start-2 sm:col-start-2 sm:row-start-4 sm:col-span-2 md:mt-2 sm:mt-2 lg:mt-2">
        <span className="hidden lg:block md:block sm:block text-sm text-gray-1100 font-bold mt-2">
          {t('availability')}
        </span>

        {isAnonymous && (
          <Button
            type="link"
            onClick={() => {
              trackEvent({
                action: 'login',
              });
              redirectLoginPage();
            }}
            className="w-fit"
          >
            {t('sign.in')}
          </Button>
        )}
        {!isAnonymous &&
          (availabilityLoading ? (
            <Skeleton rows={2} gap={[6, 6]} width={['60%', '100%']} horizontal="rtl" className="mb-8" />
          ) : (
            availability?.availabilityMessage[lang.toUpperCase()]?.map((message, index) => {
              return (
                <span key={index} className="text-sm font-normal text-gray-1200 availability-item">
                  {message}
                </span>
              );
            })
          ))}
      </div>
      <div className="sm:row-start-3 sm:col-start-2 sm:col-span-2 flex justify-center sm:justify-between sm:mt-2">
        <span className="hidden sm:block text-sm text-gray-1100 font-bold mt-2">{commonT('quantity')}</span>
        {!price && (
          <InputNumber
            preventDefault={true}
            className="w-[104px]"
            textAlign="center"
            minValue="1"
            maxValue="9999"
            value={quantity}
            disabled
            onChange={onQuantityChange}
            onBlur={onPriceInputBlur}
          />
        )}
        {price && (
          <>
            <div
              className={`justify-self-center print:hidden ${
                contactUs || product.productStatus !== 'RELEASED' ? 'hidden' : ''
              }`}
            >
              <InputNumber
                preventDefault={true}
                className="w-[104px]"
                textAlign="center"
                minValue="1"
                maxValue="9999"
                value={quantity}
                disabled={
                  promotionInfo &&
                  promotionInfo.promoType === 'FBP' &&
                  product.canApplyPromotion &&
                  product.quantity === getPromotionQuantity()
                }
                onChange={onQuantityChange}
                onBlur={onPriceInputBlur}
              />
              {isKR && (
                <div className="w-[104px] mt-1 flex justify-center">
                  {stockLoading ? (
                    <Skeleton rows={1} horizontal="rtl" />
                  ) : (
                    stockVal !== null && (
                      <Button type="link" className="p-0">
                        {commonT('stock', { count: stockVal })}
                      </Button>
                    )
                  )}
                </div>
              )}
            </div>
            <div
              className={`justify-self-center print:block ${
                contactUs || product.productStatus !== 'RELEASED' ? '' : 'hidden'
              }`}
            >
              {quantity}
            </div>
          </>
        )}
      </div>
      {price && (
        <div
          className={`flex flex-col sm:row-start-5 sm:col-start-2 sm:col-span-2 sm:mt-2 ${
            contactUs ? 'items-end sm:items-start' : ''
          }`}
        >
          {!contactUs && (
            <>
              <span className="text-sm text-gray-1100 text-right sm:flex sm:justify-between">
                <span className="font-medium">{commonT('price')}:</span>
                <span className="font-normal line-through ml-1">{getFormattedPrice(number(price.listPrice))}</span>
              </span>

              <span className="text-sm mt-[6px] text-gray-1100 text-right  sm:flex sm:justify-between">
                <span className="font-medium">{commonT('yourPrice')}:</span>
                <span className="font-normal ml-1">{getFormattedPrice(number(price.bestPrice))}</span>
              </span>

              {promotionInfo && (
                <>
                  {showDiscountDetails ? (
                    <span className="text-sm mt-[6px] text-[#E71316] text-right flex flex-row justify-end items-center">
                      {showDiscountTooltip ? (
                        <Tooltip title={discountTooltipTitle} placement="top" maxWidth={260}>
                          {discountBadge}
                        </Tooltip>
                      ) : (
                        discountBadge
                      )}
                    </span>
                  ) : shouldShowManualPromoNotApplicable || shouldForcePromoNotApplicable ? (
                    <span className="text-sm mt-[6px] text-right flex flex-row justify-end items-center">
                      <div className="flex flex-row justify-center items-center rounded-[15px] border-[1px] border-solid border-[#54545C] px-3">
                        <span className="font-medium text-[#54545C] text-sm">
                          {promotionT('promotion.list.applicable.not')}
                        </span>
                        <Tooltip
                          title={
                            shouldForcePromoNotApplicable
                              ? vbpAppliedPromotionMessage || vbpNextSetPromotionMessage
                              : price?.manualPromoNotApplicableReasonMessage ||
                                commonT(`code.${effectivePromoNotApplicableReason}`)
                          }
                          placement="top"
                          maxWidth={300}
                          className="flex justify-center items-center pl-1"
                        >
                          <i className="icon-16-question-mono text-[#0071D0] font-normal ml-1" />
                        </Tooltip>
                      </div>
                    </span>
                  ) : null}
                </>
              )}

              <span className="text-sm mt-[6px] text-gray-1100 text-right  sm:flex sm:justify-between">
                <span className="font-medium">{t('savings')}</span>
                <span className="font-normal ml-1">{getFormattedPrice(savings)}</span>
              </span>

              <span className="mt-[17px] text-gray-1100 text-right  sm:flex sm:justify-between">
                <span className="text-base leading-[22px] font-bold">{t('total.price')}</span>
                <span className="text-base font-normal ml-2">
                  {getFormattedPrice(multiply(number(price.bestPrice), product.quantity))}
                </span>
              </span>
            </>
          )}
          {contactUs && (
            <Button
              className="p-0 text-left flex items-center h-auto leading-none cursor-pointer"
              type="link"
              onClick={() => {
                trackEvent({
                  key: 'eventsData',
                  action: 'requestQuote',
                  payload: {
                    event_name: 'requestQuote',
                    request_quote_sku: product.catalogNumber,
                  },
                });
                router.push(getContactUsLink(lang));
              }}
            >
              {t('contact.us')}
            </Button>
          )}
        </div>
      )}
      {!price && (
        <div className="sm:col-start-2 sm:my-4 sm:row-start-5 h-20 sm:mb-8">
          <Skeleton rows={4} gap={[6, 6, 16]} width={['60%', '60%', '60%', '100%']} horizontal="rtl" />
        </div>
      )}
      <div className="row-start-2 col-start-3"></div>
      <Alert
        open={product.productStatus === 'DISCONTINUED'}
        type="error"
        className={`row-start-3 col-start-3 col-end-7 mt-[18px] print:hidden lg:row-start-4 sm:row-start-7 sm:col-start-2 sm:col-end-3 md:row-start-4`}
        title={null}
        content={t('product.discontinued.tip')}
      />
      <Alert
        open={product.productStatus === 'BLOCKED'}
        type="error"
        className={`row-start-3 col-start-3 col-end-7 mt-[18px] print:hidden lg:row-start-4 sm:row-start-7 sm:col-start-2 sm:col-end-3 md:row-start-4`}
        title={null}
        content={t('not.web.orderable.tips')}
      />
      {isSelected && (
        <HoldCodeMessage className="row-start-8 col-start-2 col-span-full mt-4" holdCodes={holdCodeResponse?.data} />
      )}
      <div className="row-start-9 col-start-2 col-span-full flex flex-col gap-2 mt-4">
        <Alert open={!!vbpPromotionMessage} type="info" content={vbpPromotionMessage} />
        <Alert open={!!adpPromotionMessage} type="success" content={adpPromotionMessage} />
        <Alert
          open={!vbpPromotionMessage && !adpPromotionMessage && !!getPromotionMessage()}
          type="info"
          content={getPromotionMessage()}
        />
      </div>
      <Alert
        open={
          !vbpPromotionMessage &&
          !adpPromotionMessage &&
          !getPromotionMessage() &&
          promotionInfo?.promoType === 'FBP' &&
          product?.canApplyPromotion !== undefined &&
          product.canApplyPromotion === false
        }
        type="info"
        className="row-start-8 col-start-2 col-span-full mt-4"
        content={promotionT('fbp.info')
          .replace('%t', getFBPItems()?.toString())
          .replace('%f', product?.quantity?.toString())}
      />
      <CartQtpModel
        currentItem={[
          {
            catalogNumber: product.catalogNumber || '',
            webCatalogNumber: product.webCatalogNumber,
            quantity: qtpConfirmModalInfo?.quantity || product.quantity,
          },
        ]}
        type={qtpConfirmModalInfo?.status}
        qtpConfirmModal={!!qtpConfirmModalInfo}
        handleClose={() => setQtpConfirmModalInfo(null)}
        handleSave={async () => {
          if (qtpConfirmModalInfo?.type === 'remove') {
            onRemoveProduct(false);
          } else {
            onQuantityChange(qtpConfirmModalInfo?.quantity?.toString() || '1', false);
          }
        }}
      />
    </div>
  );
};
